import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Plus,
  Trash2,
  Play,
  Scale,
  Users,
  AlertCircle,
  CheckCircle,
  XCircle,
  Info,
  DollarSign,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { calcDragAlong } from '@/utils/equityMath';
import type { DragAlongShareholder, DragAlongScenario, DragAlongResult } from '@/utils/equityMath';

type Shareholder = DragAlongShareholder;

interface SaleScenario extends DragAlongScenario {
  id: string;
}

interface ScenarioResult extends DragAlongResult {
  reasoning: string[];
}

export default function DragAlongCalculator() {
  const [shareholders, setShareholders] = useState<Shareholder[]>([
    {
      id: '1',
      name: 'Founder 1',
      shareholderType: 'Founder',
      shares: 2500000,
      hasDragAlongRight: true,
      hasTagAlongRight: false,
      isDragAlongSubject: false,
    },
    {
      id: '2',
      name: 'Founder 2',
      shareholderType: 'Founder',
      shares: 2000000,
      hasDragAlongRight: true,
      hasTagAlongRight: false,
      isDragAlongSubject: false,
    },
    {
      id: '3',
      name: 'Series A Lead',
      shareholderType: 'Investor',
      shares: 3000000,
      hasDragAlongRight: true,
      hasTagAlongRight: false,
      isDragAlongSubject: false,
      // A preference on the default cap table, so the point is visible without
      // having to know to look for it: on a modest offer the preference is paid
      // before the founders see anything.
      investmentAmount: 12000000,
      liquidationPreference: 1,
    },
    {
      id: '4',
      name: 'Angel Investors',
      shareholderType: 'Investor',
      shares: 1500000,
      hasDragAlongRight: false,
      hasTagAlongRight: true,
      isDragAlongSubject: true,
      investmentAmount: 3000000,
      liquidationPreference: 1,
    },
    {
      id: '5',
      name: 'Employee Pool',
      shareholderType: 'Employee',
      shares: 1000000,
      hasDragAlongRight: false,
      hasTagAlongRight: true,
      isDragAlongSubject: true,
    },
  ]);

  const [dragAlongThreshold, setDragAlongThreshold] = useState(50); // Percentage needed to trigger
  const [tagAlongThreshold, setTagAlongThreshold] = useState(10); // Min sale size to trigger tag-along

  const [saleScenarios, setSaleScenarios] = useState<SaleScenario[]>([
    {
      id: '1',
      name: 'Strategic Acquisition',
      buyerName: 'BigTech Corp',
      offerPrice: 50000000,
      initiatedBy: ['1', '3'],
      requiresUnanimous: false,
    },
    {
      id: '2',
      name: 'PE Buyout',
      buyerName: 'Private Equity Fund',
      offerPrice: 75000000,
      initiatedBy: ['3'],
      requiresUnanimous: false,
    },
    {
      id: '3',
      name: 'Founder Exit',
      buyerName: 'Competitor Inc',
      offerPrice: 40000000,
      initiatedBy: ['1', '2'],
      requiresUnanimous: false,
    },
  ]);

  const [results, setResults] = useState<ScenarioResult[]>([]);
  const [analyzing, setAnalyzing] = useState(false);

  const addShareholder = () => {
    const newId = String(Date.now());
    setShareholders([
      ...shareholders,
      {
        id: newId,
        name: 'New Shareholder',
        shareholderType: 'Other',
        shares: 0,
        hasDragAlongRight: false,
        hasTagAlongRight: false,
        isDragAlongSubject: false,
      },
    ]);
  };

  const updateShareholder = (
    id: string,
    field: keyof Shareholder,
    // `undefined` clears a preference field back to "ordinary shares", which is
    // distinct from a recorded zero.
    value: string | number | boolean | undefined
  ) => {
    setShareholders(
      shareholders.map((sh) => {
        if (sh.id === id) {
          return { ...sh, [field]: value };
        }
        return sh;
      })
    );
  };

  const removeShareholder = (id: string) => {
    setShareholders(shareholders.filter((sh) => sh.id !== id));
  };

  const addScenario = () => {
    const newId = String(Date.now());
    setSaleScenarios([
      ...saleScenarios,
      {
        id: newId,
        name: 'New Sale Scenario',
        buyerName: '',
        offerPrice: 0,
        initiatedBy: [],
        requiresUnanimous: false,
      },
    ]);
  };

  const updateScenario = (
    id: string,
    field: keyof SaleScenario,
    value: string | number | boolean | string[]
  ) => {
    setSaleScenarios(saleScenarios.map((sc) => (sc.id === id ? { ...sc, [field]: value } : sc)));
  };

  const removeScenario = (id: string) => {
    setSaleScenarios(saleScenarios.filter((sc) => sc.id !== id));
  };

  const toggleInitiator = (scenarioId: string, shareholderId: string) => {
    setSaleScenarios(
      saleScenarios.map((sc) => {
        if (sc.id === scenarioId) {
          const initiatedBy = sc.initiatedBy.includes(shareholderId)
            ? sc.initiatedBy.filter((id) => id !== shareholderId)
            : [...sc.initiatedBy, shareholderId];
          return { ...sc, initiatedBy };
        }
        return sc;
      })
    );
  };

  const registerShares = shareholders.reduce((sum, sh) => sum + Math.max(0, sh.shares), 0);
  const ownershipOf = (sh: Shareholder) =>
    registerShares > 0 ? (Math.max(0, sh.shares) / registerShares) * 100 : 0;

  const analyzeSale = (scenario: SaleScenario): ScenarioResult => {
    const result = calcDragAlong(shareholders, scenario, {
      dragAlongThreshold,
      tagAlongThreshold,
    });

    const initiators = result.participants.filter((p) => p.role === 'Initiator');
    const dragged = result.participants.filter((p) => p.role === 'Dragged Along');
    const tagged = result.participants.filter((p) => p.role === 'Tagged Along');

    const reasoning: string[] = [
      `Initiating shareholders: ${initiators.map((p) => p.name).join(', ') || 'none'} (${result.initiatingPct.toFixed(1)}% ownership)`,
      result.dragAlongTriggered
        ? `✓ Drag-along threshold met (${result.initiatingPct.toFixed(1)}% ≥ ${dragAlongThreshold}%)`
        : `✗ Drag-along threshold not met (${result.initiatingPct.toFixed(1)}% < ${dragAlongThreshold}%)`,
      result.initiatorsHoldDragRight
        ? '✓ Initiators have drag-along rights'
        : '✗ Initiators lack drag-along rights',
    ];

    if (result.canForceSale) {
      reasoning.push('✓ Sale can be forced via drag-along provisions');
      reasoning.push(`${dragged.length} shareholders must participate`);
    } else {
      reasoning.push('✗ Cannot force sale - requires voluntary participation');
    }

    if (tagged.length > 0) {
      reasoning.push(`${tagged.length} shareholders can exercise tag-along rights`);
    }

    reasoning.push(
      `Offer of $${(result.offerPrice / 1000000).toFixed(1)}M across ${result.totalShares.toLocaleString()} shares is $${result.pricePerShare.toFixed(4)} a share`
    );

    reasoning.push(
      result.saleViable
        ? `✓ Sale is viable (${result.totalParticipatingPct.toFixed(1)}% participating)`
        : '✗ Sale not viable (insufficient participation)'
    );

    return { ...result, reasoning };
  };

  const runAnalysis = () => {
    setAnalyzing(true);
    setTimeout(() => {
      const scenarioResults = saleScenarios.map(analyzeSale);
      setResults(scenarioResults);
      setAnalyzing(false);
    }, 800);
  };

  // Chart data
  const ownershipData = shareholders.map((sh) => ({
    name: sh.name,
    ownership: ownershipOf(sh),
    hasDragAlong: sh.hasDragAlongRight,
    hasTagAlong: sh.hasTagAlongRight,
  }));

  const rightsDistributionData = [
    { name: 'Drag-Along Rights', count: shareholders.filter((sh) => sh.hasDragAlongRight).length },
    { name: 'Tag-Along Rights', count: shareholders.filter((sh) => sh.hasTagAlongRight).length },
    {
      name: 'Drag-Along Subject',
      count: shareholders.filter((sh) => sh.isDragAlongSubject).length,
    },
    {
      name: 'No Special Rights',
      count: shareholders.filter(
        (sh) => !sh.hasDragAlongRight && !sh.hasTagAlongRight && !sh.isDragAlongSubject
      ).length,
    },
  ];

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))',
  ];

  const totalOwnership = shareholders.reduce((sum, sh) => sum + ownershipOf(sh), 0);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5" />
            Drag-Along & Tag-Along Rights Calculator
          </CardTitle>
          <CardDescription>
            Determine voting thresholds and model shareholder rights in sale scenarios
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Rights Configuration */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Drag-Along Threshold (%)</Label>
              <Input
                type="number"
                value={dragAlongThreshold}
                onChange={(e) => setDragAlongThreshold(Number(e.target.value))}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Minimum ownership % needed to trigger drag-along rights
              </p>
            </div>
            <div>
              <Label>Tag-Along Threshold (%)</Label>
              <Input
                type="number"
                value={tagAlongThreshold}
                onChange={(e) => setTagAlongThreshold(Number(e.target.value))}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Minimum sale size for tag-along rights to activate
              </p>
            </div>
          </div>

          {/* Shareholders */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Shareholders & Rights</h3>
              <Button onClick={addShareholder} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-1" /> Add Shareholder
              </Button>
            </div>
            <div className="space-y-3">
              {shareholders.map((sh) => (
                <div key={sh.id} className="p-4 border rounded-lg space-y-3">
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-3">
                      <Label className="text-xs">Name</Label>
                      <Input
                        value={sh.name}
                        onChange={(e) => updateShareholder(sh.id, 'name', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Type</Label>
                      <select
                        value={sh.shareholderType}
                        onChange={(e) =>
                          updateShareholder(
                            sh.id,
                            'shareholderType',
                            e.target.value as Shareholder['shareholderType']
                          )
                        }
                        className="w-full mt-1 px-3 py-2 border rounded-md text-sm bg-background"
                      >
                        <option value="Founder">Founder</option>
                        <option value="Investor">Investor</option>
                        <option value="Employee">Employee</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Ownership %</Label>
                      <div className="mt-1 px-3 py-2 bg-muted rounded-md text-sm">
                        {ownershipOf(sh).toFixed(2)}%
                      </div>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Shares</Label>
                      <div className="mt-1 px-3 py-2 bg-muted rounded-md text-sm">
                        {sh.shares.toLocaleString()}
                      </div>
                    </div>
                    {/* Preference terms. Without these the proceeds below were a
                        flat split by share count, which is not how a sale pays
                        out — see calcDragAlong. Left blank, the holder is
                        ordinary and nothing changes. */}
                    <div className="col-span-1">
                      <Label className="text-xs">Invested</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="—"
                        value={sh.investmentAmount ?? ''}
                        onChange={(e) =>
                          updateShareholder(
                            sh.id,
                            'investmentAmount',
                            e.target.value === '' ? undefined : Math.max(0, Number(e.target.value))
                          )
                        }
                        className="mt-1"
                      />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-xs">Pref ×</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        placeholder="—"
                        value={sh.liquidationPreference ?? ''}
                        onChange={(e) =>
                          updateShareholder(
                            sh.id,
                            'liquidationPreference',
                            e.target.value === '' ? undefined : Math.max(0, Number(e.target.value))
                          )
                        }
                        className="mt-1"
                      />
                    </div>
                    {/* The type badge that sat here duplicated the Type dropdown
                        two columns to its left, and the row is a 12-column grid
                        with no space left for the preference inputs. */}
                    <div className="col-span-1 flex justify-end">
                      <Button onClick={() => removeShareholder(sh.id)} size="sm" variant="ghost">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={sh.hasDragAlongRight}
                        onCheckedChange={(checked) =>
                          updateShareholder(sh.id, 'hasDragAlongRight', checked)
                        }
                        id={`drag-${sh.id}`}
                      />
                      <Label htmlFor={`drag-${sh.id}`} className="text-sm cursor-pointer">
                        Has Drag-Along Right
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={sh.hasTagAlongRight}
                        onCheckedChange={(checked) =>
                          updateShareholder(sh.id, 'hasTagAlongRight', checked)
                        }
                        id={`tag-${sh.id}`}
                      />
                      <Label htmlFor={`tag-${sh.id}`} className="text-sm cursor-pointer">
                        Has Tag-Along Right
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={sh.isDragAlongSubject}
                        onCheckedChange={(checked) =>
                          updateShareholder(sh.id, 'isDragAlongSubject', checked)
                        }
                        id={`subject-${sh.id}`}
                      />
                      <Label htmlFor={`subject-${sh.id}`} className="text-sm cursor-pointer">
                        Subject to Drag-Along
                      </Label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 p-3 bg-muted/50 rounded-lg">
              <span className="text-sm text-muted-foreground">Total Ownership:</span>
              <span
                className={`ml-2 font-bold ${Math.abs(totalOwnership - 100) > 0.1 ? 'text-destructive' : ''}`}
              >
                {totalOwnership.toFixed(1)}%
              </span>
              {Math.abs(totalOwnership - 100) > 0.1 && (
                <span className="ml-2 text-xs text-destructive">⚠ Should equal 100%</span>
              )}
            </div>
          </div>

          {/* Sale Scenarios */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Sale Scenarios</h3>
              <Button onClick={addScenario} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-1" /> Add Scenario
              </Button>
            </div>
            <div className="space-y-3">
              {saleScenarios.map((scenario) => (
                <div key={scenario.id} className="p-4 border rounded-lg space-y-3">
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-3">
                      <Label className="text-xs">Scenario Name</Label>
                      <Input
                        value={scenario.name}
                        onChange={(e) => updateScenario(scenario.id, 'name', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Buyer Name</Label>
                      <Input
                        value={scenario.buyerName}
                        onChange={(e) => updateScenario(scenario.id, 'buyerName', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Offer Price ($)</Label>
                      <Input
                        type="number"
                        value={scenario.offerPrice}
                        onChange={(e) =>
                          updateScenario(scenario.id, 'offerPrice', Number(e.target.value))
                        }
                        className="mt-1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Price/Share ($)</Label>
                      <div className="mt-1 px-3 py-2 bg-muted rounded-md text-sm">
                        {registerShares > 0
                          ? `${(scenario.offerPrice / registerShares).toFixed(4)}`
                          : '—'}
                      </div>
                    </div>
                    <div className="col-span-1 flex items-end">
                      <div className="flex items-center gap-2 pb-2">
                        <Switch
                          checked={scenario.requiresUnanimous}
                          onCheckedChange={(checked) =>
                            updateScenario(scenario.id, 'requiresUnanimous', checked)
                          }
                          id={`unanimous-${scenario.id}`}
                        />
                      </div>
                    </div>
                    <div className="col-span-1 flex justify-end items-end pb-2">
                      <Button onClick={() => removeScenario(scenario.id)} size="sm" variant="ghost">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs mb-2 block">Initiating Shareholders:</Label>
                    <div className="flex flex-wrap gap-2">
                      {shareholders.map((sh) => (
                        <Badge
                          key={sh.id}
                          variant={scenario.initiatedBy.includes(sh.id) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => toggleInitiator(scenario.id, sh.id)}
                        >
                          {sh.name} ({ownershipOf(sh).toFixed(1)}%)
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <strong>Rights Explained:</strong> Drag-along rights allow majority shareholders to
              force minorities to sell. Tag-along rights allow minorities to join when majorities
              sell. Configure thresholds and rights above to model different scenarios.
            </AlertDescription>
          </Alert>

          <Button onClick={runAnalysis} disabled={analyzing} className="w-full">
            <Play className="h-4 w-4 mr-2" />
            {analyzing ? 'Analyzing...' : 'Analyze Sale Scenarios'}
          </Button>
        </CardContent>
      </Card>

      {results.length > 0 && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Current Ownership & Rights</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3">Ownership Distribution</h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={ownershipData}
                        dataKey="ownership"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={(entry) => `${entry.name}: ${entry.ownership}%`}
                      >
                        {ownershipData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h4 className="font-semibold mb-3">Rights Distribution</h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={rightsDistributionData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="hsl(var(--chart-2))" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Scenario Analysis Results</CardTitle>
              <CardDescription>
                Detailed breakdown of sale mechanics and shareholder participation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="0">
                <TabsList
                  className="grid w-full"
                  style={{ gridTemplateColumns: `repeat(${results.length}, 1fr)` }}
                >
                  {results.map((result, idx) => (
                    <TabsTrigger key={idx} value={String(idx)}>
                      {result.scenarioName}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {results.map((result, idx) => (
                  <TabsContent key={idx} value={String(idx)} className="space-y-4">
                    <div className="grid md:grid-cols-3 gap-4">
                      <Card>
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Buyer</span>
                          </div>
                          <p className="text-lg font-bold">{result.buyerName}</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-2 mb-2">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Offer Price</span>
                          </div>
                          <p className="text-lg font-bold">
                            ${(result.offerPrice / 1000000).toFixed(1)}M
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-2 mb-2">
                            {result.saleViable ? (
                              <CheckCircle className="h-4 w-4 text-success" />
                            ) : (
                              <XCircle className="h-4 w-4 text-destructive" />
                            )}
                            <span className="text-sm text-muted-foreground">Sale Status</span>
                          </div>
                          <p
                            className={`text-lg font-bold ${result.saleViable ? 'text-success' : 'text-destructive'}`}
                          >
                            {result.saleViable ? 'Viable' : 'Not Viable'}
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    <Alert variant={result.canForceSale ? 'default' : 'destructive'}>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Drag-Along Status:</strong>{' '}
                        {result.canForceSale
                          ? `✓ Sale can be forced (${result.initiatingPct.toFixed(1)}% initiating)`
                          : `✗ Cannot force sale (${result.initiatingPct.toFixed(1)}% < ${dragAlongThreshold}% threshold)`}
                      </AlertDescription>
                    </Alert>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-success" />
                          Must Participate (
                          {
                            result.participants.filter(
                              (p) => p.role === 'Initiator' || p.role === 'Dragged Along'
                            ).length
                          }
                          )
                        </h4>
                        <div className="space-y-1">
                          {result.participants
                            .filter((p) => p.role === 'Initiator' || p.role === 'Dragged Along')
                            .map((sh, i) => (
                              <div key={i} className="text-sm flex justify-between">
                                <span>{sh.name}</span>
                                <Badge variant="secondary">{sh.ownershipPct.toFixed(1)}%</Badge>
                              </div>
                            ))}
                        </div>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                          <Users className="h-4 w-4 text-primary" />
                          Can Tag Along (
                          {result.participants.filter((p) => p.role === 'Tagged Along').length})
                        </h4>
                        <div className="space-y-1">
                          {result.participants
                            .filter((p) => p.role === 'Tagged Along')
                            .map((sh, i) => (
                              <div key={i} className="text-sm flex justify-between">
                                <span>{sh.name}</span>
                                <Badge variant="outline">{sh.ownershipPct.toFixed(1)}%</Badge>
                              </div>
                            ))}
                        </div>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-muted-foreground" />
                          Won&apos;t Participate (
                          {result.participants.filter((p) => p.role === 'Not Participating').length}
                          )
                        </h4>
                        <div className="space-y-1">
                          {result.participants
                            .filter((p) => p.role === 'Not Participating')
                            .map((sh, i) => (
                              <div key={i} className="text-sm flex justify-between">
                                <span className="text-muted-foreground">{sh.name}</span>
                                <Badge variant="outline">{sh.ownershipPct.toFixed(1)}%</Badge>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-1">Proceeds Distribution</h4>
                      <p className="text-xs text-muted-foreground mb-3">
                        Paid through the liquidation waterfall: preferences first, then the residual
                        by shares. Holders with no preference recorded simply share pro rata. This
                        was a flat split by share count, which overstated what founders receive
                        whenever a preference ranked ahead of them.
                      </p>
                      <div className="rounded-lg border overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-semibold">
                                Shareholder
                              </th>
                              <th className="px-4 py-2 text-left text-sm font-semibold">Type</th>
                              <th className="px-4 py-2 text-center text-sm font-semibold">
                                Status
                              </th>
                              <th className="px-4 py-2 text-right text-sm font-semibold">
                                Proceeds
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {result.participants.map((dist, i) => (
                              <tr key={i} className="hover:bg-muted/30">
                                <td className="px-4 py-3 font-medium">{dist.name}</td>
                                <td className="px-4 py-3 text-sm">{dist.role}</td>
                                <td className="px-4 py-3 text-center">
                                  {dist.participates ? (
                                    <CheckCircle className="h-4 w-4 text-success inline" />
                                  ) : (
                                    <XCircle className="h-4 w-4 text-muted-foreground inline" />
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right font-bold">
                                  ${(dist.proceeds / 1000000).toFixed(2)}M
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Analysis Reasoning:</strong>
                        <ul className="mt-2 space-y-1 text-sm ml-4 list-disc">
                          {result.reasoning.map((reason, i) => (
                            <li key={i}>{reason}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
